import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import styles from "./Horizontalbarchart.module.css";

Chart.register(...registerables);

/** Number of color slots declared in Horizontalbarchart.module.css. */
const SERIES_SLOTS = 5;

/**
 * Chart.js plugin: draws value inside each segment + the row total at the end.
 */
const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    // value suffix ("%" for percentage charts, "" for plain counts) — set via options.plugins.valueLabels.suffix
    const suffix = chart.options?.plugins?.valueLabels?.suffix ?? "%";

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const value = Number(dataset.data[index]) || 0;
        if (value <= 0) return;
        const segmentWidth = Math.abs(bar.x - bar.base);
        if (segmentWidth < 28) return;

        ctx.save();
        ctx.fillStyle = "#ffffff";
        ctx.font =
          "600 12px ui-sans-serif, system-ui, -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${value}${suffix}`, (bar.x + bar.base) / 2, bar.y);
        ctx.restore();
      });
    });

    const lastIdx = data.datasets.length - 1;
    const lastMeta = chart.getDatasetMeta(lastIdx);
    lastMeta.data.forEach((bar, index) => {
      const total = data.datasets.reduce(
        (sum, ds) => sum + (Number(ds.data[index]) || 0),
        0,
      );
      if (total <= 0) return;
      ctx.save();
      ctx.fillStyle = "#111827";
      ctx.font = "700 13px ui-sans-serif, system-ui, -apple-system, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(`${total}${suffix}`, bar.x + 8, bar.y);
      ctx.restore();
    });
  },
};

/**
 * Build a Chart.js scriptable `backgroundColor` function that returns a
 * gradient anchored to the ROW's full range — from x=0 to x=rowTotal.
 *
 * Rendering direction:
 *   - LEFT  edge → `lightColor`
 *   - RIGHT edge → `darkColor`
 *
 * IMPORTANT: scriptable callbacks fire during the very first layout pass,
 * before the x-scale has computed its pixel range. In that window
 * `getPixelForValue()` can return NaN / Infinity, which would crash
 * `createLinearGradient`. We guard with Number.isFinite() and fall back to
 * the solid `darkColor`; Chart.js re-runs the callback on the next pass once
 * the scale is ready and the real gradient appears.
 */
const makeRowGradient = (rowTotals, darkColor, lightColor) => (context) => {
  const { chart, dataIndex } = context;
  if (!chart?.scales?.x || !chart?.ctx) return darkColor;

  const rowTotal = rowTotals[dataIndex] || 0;
  if (rowTotal <= 0) return darkColor;

  const xScale = chart.scales.x;
  const startX = xScale.getPixelForValue(0);
  const endX = xScale.getPixelForValue(rowTotal);

  if (!Number.isFinite(startX) || !Number.isFinite(endX) || endX - startX < 1) {
    return darkColor;
  }

  const g = chart.ctx.createLinearGradient(startX, 0, endX, 0);
  g.addColorStop(0, lightColor); // LEFT
  g.addColorStop(1, darkColor); //  RIGHT
  return g;
};

/**
 * Reusable stacked horizontal bar chart.
 *
 * Colors come from `Horizontalbarchart.module.css` — each dataset is
 * auto-assigned the next series slot (`--hbar-series-N-dark/light`).
 * Override those custom properties in CSS to retheme the chart.
 *
 * @param {string}   title         Card heading
 * @param {string[]} labels        Y-axis categories
 * @param {Array}    datasets      [{ label, data }] — no color props needed
 * @param {boolean}  showViewAll   Show the "View All" action (default true)
 * @param {function} onViewAll     Callback for the View All button
 * @param {string}   viewAllLabel  Label for the View All button
 * @param {number}   height        Chart canvas height in px (default 320)
 * @param {boolean}  stacked       Stack segments (default true)
 * @param {boolean}  loading       Loading state
 * @param {string}   error         Error message (renders error UI)
 * @param {string}   emptyMessage  Shown when datasets are empty
 * @param {React.ReactNode} icon   Icon to display in the card header
 * @param {string}   valueSuffix   Text after every value: "%" (default) for percentage
 *                                 charts, "" when the datasets are plain counts
 */
const HorizontalBarChart = ({
  title = "Assessment Progress Tracker",
  labels = [],
  datasets = [],
  showViewAll = true,
  icon,
  onViewAll,
  viewAllLabel = "View All",
  height = 320,
  stacked = true,
  loading = false,
  error = null,
  emptyMessage = "No data available",
  xAxisLabel = null,
  valueSuffix = "%",
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const hasData =
    Array.isArray(labels) &&
    labels.length > 0 &&
    Array.isArray(datasets) &&
    datasets.length > 0;

  useEffect(() => {
    if (!canvasRef.current || !hasData || loading || error) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const ctx = canvasRef.current.getContext("2d");

    /**
     * CSS custom properties cascade — the canvas is nested inside .hbarCard,
     * so it inherits the --hbar-series-N-* tokens declared there. We read
     * them off the canvas directly; getComputedStyle resolves through the
     * full `var()` chain back to the palette values in app.css.
     */
    const cardStyle = getComputedStyle(canvasRef.current);
    const getToken = (name) => cardStyle.getPropertyValue(name).trim();

    // Pre-compute the total of each row so all datasets in that row share the
    // same gradient scale (anchored at 0 and at the row's total value).
    const rowTotals = labels.map((_, idx) =>
      datasets.reduce((sum, ds) => sum + (Number(ds.data[idx]) || 0), 0),
    );

    const chartDatasets = datasets.map((ds, index) => {
      const slot = (index % SERIES_SLOTS) + 1;
      const darkColor = getToken(`--hbar-series-${slot}-dark`);
      const lightColor = getToken(`--hbar-series-${slot}-light`);
      const gradientFn = makeRowGradient(rowTotals, darkColor, lightColor);

      return {
        label: ds.label,
        data: ds.data,
        backgroundColor: gradientFn,
        hoverBackgroundColor: gradientFn,
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 22,
      };
    });

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: { labels, datasets: chartDatasets },
      plugins: [valueLabelsPlugin],
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 48, top: 8, bottom: 8 } },
        plugins: {
          valueLabels: { suffix: valueSuffix },
          legend: {
            position: "bottom",
            align: "center",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              boxWidth: 10,
              boxHeight: 10,
              padding: 22,
              font: { size: 13, weight: "500" },
              color: "#4b5563",
            },
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            padding: 12,
            cornerRadius: 8,
            titleFont: { weight: "600", size: 13 },
            bodyFont: { size: 12 },
            boxPadding: 4,
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.x ?? ctx.raw ?? 0;   // horizontal bar → value is parsed.x
                return ` ${ctx.dataset.label}: ${v}${valueSuffix}`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked,
            beginAtZero: true,
            grid: { color: "rgba(0, 0, 0, 0.06)", drawBorder: false },
            ticks: { color: "#9ca3af", font: { size: 12 }, callback: (value) => `${value}${valueSuffix}`, },
            border: { display: false },
            title: {                                   // ← add
              display: !!xAxisLabel,
              text: xAxisLabel || "",
              color: "#6b7280",
              font: { size: 13, weight: "600" },
              padding: { top: 10 },
            },
          },
          y: {
            stacked,
            grid: { display: false, drawBorder: false },
            ticks: { color: "#374151", font: { size: 13, weight: "500" } },
            border: { display: false },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [labels, datasets, stacked, hasData, loading, error, valueSuffix, xAxisLabel]);

  return (
    <div className={styles.hbarCard}>
      <div className={styles.hbarHeader}>
        <div className={styles.hbarTitle}>
          <span className={styles.hbarIconWrap}>{icon}</span>
          <h3 className="chartTitle">{title}</h3>
        </div>
        {showViewAll && (
          <button
            className={styles.hbarViewAll}
            onClick={onViewAll}
            type="button"
          >
            {viewAllLabel}
          </button>
        )}
      </div>

      <div className={styles.hbarChart} style={{ height }}>
        {loading && (
          <div className={styles.hbarState}>
            <div className={styles.hbarSpinner} />
          </div>
        )}
        {!loading && error && (
          <div className={`${styles.hbarState} ${styles.hbarError}`}>
            {error}
          </div>
        )}
        {!loading && !error && !hasData && (
          <div className={styles.hbarState}>{emptyMessage}</div>
        )}
        {!loading && !error && hasData && <canvas ref={canvasRef} />}
      </div>
    </div>
  );
};

export default HorizontalBarChart;
